/**
 * Local urgency/importance classifier.
 *
 * Scores each task candidate on two axes (0–100) using weighted lexical
 * signals and deadline detection, then maps the pair onto the four zones of
 * the priority matrix:
 *
 *   urgent  & important  → zone1  Critical & Immediate
 *   !urgent & important  → zone2  Strategic & Growth
 *   urgent  & !important → zone3  Distractions & Demands
 *   !urgent & !important → zone4  Time Drains & Diversions
 */

import type { ClassifiedTask, ZoneId } from '../types';
import { segment } from './segment';

interface Signal {
  pattern: RegExp;
  urgency?: number;
  importance?: number;
}

const SIGNALS: Signal[] = [
  // --- Crisis / breakage: urgent AND important ---
  { pattern: /\b(crash(es|ed|ing)?|outage|down|broken?|not working|fail(ing|ed|ure)?s?)\b/i, urgency: 45, importance: 30 },
  { pattern: /\b(urgent(ly)?|asap|emergency|critical|crisis|immediately|right away|on fire)\b/i, urgency: 50, importance: 15 },
  { pattern: /\b(severe|serious|major)\b/i, urgency: 25, importance: 20 },
  { pattern: /\b(bug|hotfix|incident|escalat(ed|ion)|security|breach|leak)\b/i, urgency: 30, importance: 25 },
  { pattern: /\b(overdue|late|past due|missed|expir(es|ed|ing)|final notice)\b/i, urgency: 40, importance: 15 },
  { pattern: /\b(pay|bill|invoice|tax(es)?|rent|deadline)\b/i, urgency: 25, importance: 20 },
  { pattern: /\b(doctor|dentist|medical|health|medication|prescription)\b/i, urgency: 20, importance: 30 },

  // --- Strategic / growth: important, usually not urgent ---
  { pattern: /\b(plan(ning)?|strategy|strategic|roadmap|vision|goal|okr)\b/i, importance: 40 },
  { pattern: /\b(review|contract|proposal|budget|forecast|report|audit)\b/i, importance: 35 },
  { pattern: /\b(launch|ship|release|campaign|marketing|growth)\b/i, importance: 35, urgency: 10 },
  { pattern: /\b(learn(ing)?|study|course|read(ing)? (a )?book|research|skill)\b/i, importance: 30 },
  { pattern: /\b(exercise|workout|gym|run(ning)?|meditat(e|ion)|sleep)\b/i, importance: 30 },
  { pattern: /\b(design|architect(ure)?|refactor|prototype|draft|write|build|create)\b/i, importance: 25 },
  { pattern: /\b(1[- ]?on[- ]?1s?|mentor(ing)?|hire|hiring|interview|team)\b/i, importance: 25 },
  { pattern: /\b(invest(ment)?|savings?|retirement|portfolio)\b/i, importance: 30 },
  { pattern: /\b(prep(are)?|presentation|pitch|demo|keynote)\b/i, importance: 25, urgency: 10 },

  // --- Interruptions / demands: urgent-ish, low personal importance ---
  { pattern: /\b(email|emails|inbox|slack|messages?|texts?|dms?)\b/i, urgency: 20, importance: -15 },
  { pattern: /\b(call back|return (a )?call|reply|respond|rsvp)\b/i, urgency: 25, importance: -5 },
  { pattern: /\b(errands?|groceries|grocery|pick ?up|drop ?off|dry ?cleaning|laundry)\b/i, urgency: 15, importance: -10 },
  { pattern: /\b(meeting|sync|standup|status update)\b/i, urgency: 15, importance: -5 },
  { pattern: /\b(unsubscribe|archive|file (away)?|tidy|declutter)\b/i, urgency: 5, importance: -15 },

  // --- Time drains: neither ---
  { pattern: /\b(scroll(ing)?|social media|instagram|tiktok|twitter|x\.com|facebook|reddit)\b/i, urgency: -20, importance: -40 },
  { pattern: /\b(netflix|binge|youtube|doom ?scroll(ing)?|mindless(ly)?|procrastinat(e|ing|ion))\b/i, urgency: -20, importance: -40 },
  { pattern: /\b(random|pointless|waste|killing time|bored(om)?)\b/i, urgency: -10, importance: -25 },
];

const DEADLINE_PATTERNS: { pattern: RegExp; urgency: number }[] = [
  { pattern: /\b(today|tonight|by (the )?end of (the )?day|eod|this morning|this afternoon|in an? hour)\b/i, urgency: 45 },
  { pattern: /\b(tomorrow|by tmrw|first thing)\b/i, urgency: 35 },
  { pattern: /\bby (mon|tues|wednes|thurs|fri|satur|sun)day\b/i, urgency: 28 },
  { pattern: /\bthis week\b/i, urgency: 22 },
  { pattern: /\b(next week|by next)\b/i, urgency: 12 },
  { pattern: /\b(next month|this month|by (the )?end of (the )?month|eom)\b/i, urgency: 8 },
  { pattern: /\bby \d{1,2}(:\d{2})?\s?(am|pm)\b/i, urgency: 40 },
  { pattern: /\bbefore (tonight|tomorrow|my|the)\b/i, urgency: 30 },
  // --- Explicit calendar commitments (tester feedback: notebook dates like
  // "MON 20th", "Tues 21st", "9:30 am Wed" are hard commitments, not hints) ---
  { pattern: /\b(mon|tue|tues|wed|wednes|thu|thur|thurs|fri|sat|satur|sun)(day)?\.?,?\s+\d{1,2}(st|nd|rd|th)?\b/i, urgency: 30 },
  { pattern: /\b\d{1,2}(st|nd|rd|th)\s+(of\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i, urgency: 25 },
  { pattern: /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(st|nd|rd|th)?\b/i, urgency: 25 },
  { pattern: /\b(this\s+)?(mon|tues|wednes|thurs|fri|satur|sun)day\b/i, urgency: 18 },
  { pattern: /\b(at\s+)?\d{1,2}(:\d{2})\s?(am|pm)\b/i, urgency: 15 },
];

export interface ScoreResult {
  urgency: number;
  importance: number;
  deadlineHint?: string;
}

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** Score a single task string on the urgency and importance axes. */
export function score(text: string): ScoreResult {
  // Neutral baseline: an unrecognised task reads as a modest to-do.
  let urgency = 22;
  let importance = 38;
  let deadlineHint: string | undefined;

  for (const s of SIGNALS) {
    if (s.pattern.test(text)) {
      urgency += s.urgency ?? 0;
      importance += s.importance ?? 0;
    }
  }

  for (const d of DEADLINE_PATTERNS) {
    const m = text.match(d.pattern);
    if (m) {
      urgency += d.urgency;
      if (!deadlineHint) deadlineHint = m[0];
    }
  }

  return { urgency: clamp(urgency), importance: clamp(importance), deadlineHint };
}

export const URGENCY_THRESHOLD = 45;
export const IMPORTANCE_THRESHOLD = 40;

export function toZone(urgency: number, importance: number): ZoneId {
  const urgent = urgency >= URGENCY_THRESHOLD;
  const important = importance >= IMPORTANCE_THRESHOLD;
  if (urgent && important) return 'zone1';
  if (!urgent && important) return 'zone2';
  if (urgent && !important) return 'zone3';
  return 'zone4';
}

/** Full local classification pass: segment → score → zone. */
export function classifyDump(rawInput: string): ClassifiedTask[] {
  return segment(rawInput).map((text) => {
    const { urgency, importance, deadlineHint } = score(text);
    return { text, zone: toZone(urgency, importance), urgency, importance, deadlineHint };
  });
}
